import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { runA11yScan } from "./runAxeScan";
import type { A11yScanOptions, A11yScanResult } from "./types";
import type { NodeResult } from "axe-core";
import { A11yResultsPanel } from "./components/A11yResultsPanel";
import { A11yCheckButton } from "./components/A11yCheckButton";
import {
  prepareHtmlFragmentHost,
  type HtmlFragmentHostOptions
} from "./htmlFragmentHost";
import { sanitizeScanHtml } from "./sanitizeScanHtml";

declare global {
  interface Window {
    piA11y?: PiA11yApi;
    CKEDITOR?: any;
  }
}

interface PanelState {
  scanResult: A11yScanResult | null;
  isScanning: boolean;
  scanError: string | null;
  lastTarget: { element: Element; options: A11yScanOptions } | null;
}

interface PiA11yApi {
  scanEditor: (editorName: string) => Promise<void>;
  scanElement: (el: Element, options?: A11yScanOptions) => Promise<void>;
  /**
   * Scan raw HTML authored in a code editor. Renders it into an offscreen
   * sandbox that mirrors the target public layout, then scans that. Used by the
   * hub custom header/footer and the account-level Custom Styling editors.
   */
  scanHtmlFragment: (
    options: HtmlFragmentHostOptions & {
      onHighlightNode?: (node: NodeResult) => void;
      panelContainer?: HTMLElement | null;
    }
  ) => Promise<void>;
  sanitizeScanHtml: (html: string) => string;
  closePanel: () => void;
  mountButton: (
    host: HTMLElement,
    opts: {
      onClick: () => void;
      getState: () => { busy: boolean; active: boolean };
    }
  ) => () => void;
  /**
   * Returns a snapshot of the last scan result plus the CKEditor instance name
   * that triggered it (null for non-CKE scans). Used by the piaccessibility
   * plugin to update the toolbar badge without polling.
   */
  getLastResult: () => {
    scanResult: A11yScanResult | null;
    isScanning: boolean;
    lastEditorName: string | null;
  };
  /**
   * Run a scan for the named CKEditor instance and update the toolbar badge
   * via `piA11yStateChange`, but do NOT open the floating panel if it is
   * currently hidden. If the panel is already open for this editor the scan
   * also refreshes the panel content. Used for debounced change-event scans,
   * the initial page-load badge, and the colour-picker MutationObserver.
   */
  scanEditorBadgeOnly: (editorName: string) => Promise<void>;
  /** Returns a value that increments after each state change (consumers may listen via `addEventListener("piA11yStateChange")`). */
  readonly version: number;
}

// -----------------------------------------------------------------------------
// Singleton React root for the floating panel.
// -----------------------------------------------------------------------------

let panelHost: HTMLDivElement | null = null;
let panelRoot: Root | null = null;
let state: PanelState = {
  scanResult: null,
  isScanning: false,
  scanError: null,
  lastTarget: null
};
let stateVersion = 0;

// Incremented on each scan start (and on close); stale async results are
// discarded if the id no longer matches. Mirrors the guard in useA11yScan.
let scanSeq = 0;

// Tracks which CKEditor instance name triggered the most recent scan so the
// plugin can match piA11yStateChange events to its own editor for badge updates.
let lastEditorName: string | null = null;

let fragmentHostId: string | null = null;

// -----------------------------------------------------------------------------
// Highlight: resolves an axe node.target selector array to a DOM element in the
// correct document (host page or CKEditor iframe), applies a temporary outline,
// and (for CKE4) selects the node inside the editor.
// -----------------------------------------------------------------------------

const HIGHLIGHT_STYLE_ID = "pia11y-hl-styles";
const HIGHLIGHT_ATTR = "data-pia11y-hl";

let hlCleanup: (() => void) | null = null;

function clearHighlight(): void {
  hlCleanup?.();
  hlCleanup = null;
}

function resolveScanDoc(): Document {
  const ft = state.lastTarget;
  if (!ft) return document;
  const frame = ft.options?.frame;
  if (frame?.contentDocument) return frame.contentDocument;
  return ft.element.ownerDocument ?? document;
}

function highlightTarget(target: (string | string[])[]): void {
  clearHighlight();
  const scanDoc = resolveScanDoc();

  if (!scanDoc.getElementById(HIGHLIGHT_STYLE_ID)) {
    const s = scanDoc.createElement("style");
    s.id = HIGHLIGHT_STYLE_ID;
    s.textContent =
      `[${HIGHLIGHT_ATTR}]{` +
      "outline:3px solid #ef4444!important;" +
      "outline-offset:2px!important;" +
      "border-radius:2px!important;}";
    (scanDoc.head ?? scanDoc.documentElement).appendChild(s);
  }

  // Walk the axe target array to find the first resolvable element.
  let el: Element | null = null;
  outer: for (let i = 0; i < target.length; i++) {
    const part = target[i];
    if (typeof part !== "string") continue;
    const next = target[i + 1];
    if (Array.isArray(next)) {
      // Nested iframe target: part=iframe selector, next=inner selectors.
      try {
        const iframeEl = scanDoc.querySelector<HTMLIFrameElement>(part);
        const iframeDoc = iframeEl?.contentDocument;
        if (iframeDoc) {
          for (const innerSel of next) {
            if (typeof innerSel !== "string") continue;
            try {
              el = iframeDoc.querySelector(innerSel);
              if (el) break outer;
            } catch {
              /* invalid selector */
            }
          }
        }
      } catch {
        /* invalid selector */
      }
      i++;
    } else {
      try {
        el = scanDoc.querySelector(part);
        if (el) break;
      } catch {
        /* invalid selector */
      }
    }
  }

  if (!el) return;

  el.setAttribute(HIGHLIGHT_ATTR, "");
  el.scrollIntoView({ behavior: "smooth", block: "center" });

  // For CKE4 surfaces, also select the node inside the editor so the cursor
  // lands there and the toolbar reflects the element's formatting.
  if (lastEditorName) {
    const cke = window.CKEDITOR;
    const editor = cke?.instances?.[lastEditorName];
    if (editor) {
      try {
        const ckNode = new cke.dom.element(el);
        const range = editor.createRange();
        range.selectNode(ckNode);
        editor.getSelection().selectRanges([range]);
        editor.focus();
      } catch {
        /* non-fatal */
      }
    }
  }

  const captured = el;
  const timer = setTimeout(
    () => captured.removeAttribute(HIGHLIGHT_ATTR),
    2200
  );
  hlCleanup = () => {
    clearTimeout(timer);
    captured.removeAttribute(HIGHLIGHT_ATTR);
  };
}

// On a node-highlight click: run the DOM outline, then defer to any host
// `onHighlightNode` (lets offscreen-sandbox surfaces point at the real source).
function handleNodeClick(
  target: (string | string[])[],
  node?: NodeResult
): void {
  highlightTarget(target);
  const cb = state.lastTarget?.options?.onHighlightNode;
  if (cb && node) {
    try {
      cb(node);
    } catch {
      /* host highlight is best-effort; never let it break the panel */
    }
  }
}

function ensurePanelHost(container?: HTMLElement | null): HTMLDivElement {
  const parent = container ?? document.body;
  if (panelHost && panelHost.isConnected) {
    if (panelHost.parentElement !== parent) parent.appendChild(panelHost);
    return panelHost;
  }
  const host = document.createElement("div");
  host.id = "pia11y-panel-root";
  host.setAttribute("data-pia11y-root", "");
  // `position: fixed` so the panel floats over any host - the shared panel
  // itself uses `position: absolute` against this fixed wrapper.
  host.style.cssText =
    "position:fixed;inset:0;pointer-events:none;z-index:9999;";
  parent.appendChild(host);
  panelHost = host;
  panelRoot = createRoot(host);
  return host;
}

function setState(patch: Partial<PanelState>, skipRender = false): void {
  state = { ...state, ...patch };
  stateVersion++;
  if (!skipRender) renderPanel();
  window.dispatchEvent(new CustomEvent("piA11yStateChange"));
}

function renderPanel(): void {
  if (!panelRoot) return;
  const showPanel = !!(state.lastTarget || state.scanError || state.isScanning);
  if (!showPanel) {
    panelRoot.render(null);
    return;
  }
  panelRoot.render(
    <A11yResultsPanel
      scanResult={state.scanResult}
      isScanning={state.isScanning}
      scanError={state.scanError}
      onRescan={() => {
        if (state.lastTarget) {
          void runScan(state.lastTarget.element, state.lastTarget.options);
        }
      }}
      onClose={closePanel}
      onNodeClick={handleNodeClick}
    />
  );
}

async function runScan(
  el: Element,
  options: A11yScanOptions = {}
): Promise<void> {
  const thisScan = ++scanSeq;
  clearHighlight();
  ensurePanelHost(options.panelContainer);
  const frame = options.frame;
  if (options.inertAfterScan) frame?.removeAttribute("inert");
  setState({
    isScanning: true,
    scanError: null,
    lastTarget: { element: el, options }
  });
  try {
    const result = await runA11yScan(el, options);
    if (thisScan !== scanSeq) return;
    setState({ isScanning: false, scanResult: result });
  } catch (err) {
    if (thisScan !== scanSeq) return;
    setState({
      isScanning: false,
      scanError: err instanceof Error ? err.message : "Scan failed"
    });
  } finally {
    if (options.inertAfterScan) frame?.setAttribute("inert", "");
  }
}

async function scanHtmlFragment(
  options: HtmlFragmentHostOptions & {
    onHighlightNode?: (node: NodeResult) => void;
    panelContainer?: HTMLElement | null;
  }
): Promise<void> {
  // Scanning a fragment is never attributed to a CKEditor instance; clear the
  // name so panel highlights don't try to select nodes in an unrelated editor.
  lastEditorName = null;
  ensurePanelHost(options.panelContainer);
  fragmentHostId = options.hostId;
  setState({ isScanning: true, scanError: null });
  try {
    const iframe = await prepareHtmlFragmentHost(options);
    await runScan(iframe, {
      frame: iframe,
      onHighlightNode: options.onHighlightNode,
      panelContainer: options.panelContainer,
      inertAfterScan: true
    });
  } catch (err) {
    setState({
      isScanning: false,
      scanError: err instanceof Error ? err.message : "Scan failed"
    });
  }
}

function closePanel(): void {
  // Invalidate any in-flight scan so it cannot repopulate a closed panel.
  scanSeq++;
  clearHighlight();
  if (fragmentHostId) {
    document.getElementById(fragmentHostId)?.remove();
    fragmentHostId = null;
  }
  setState({
    scanResult: null,
    scanError: null,
    lastTarget: null,
    isScanning: false
  });
}

// -----------------------------------------------------------------------------
// CKEditor 4 helpers.
// -----------------------------------------------------------------------------

function resolveCkeIframe(editor: any): HTMLIFrameElement | undefined {
  // CKE4 inline editing: no iframe, scan editable element directly.
  // wysiwygarea: editor.window.getFrame() returns CKEDITOR.dom.element wrapping the iframe.
  try {
    const win = editor?.window;
    if (!win) return undefined;
    const frameWrap = win.getFrame?.();
    const native = frameWrap?.$ as HTMLIFrameElement | undefined;
    return native;
  } catch {
    return undefined;
  }
}

function resolveCkeTarget(editor: any): {
  element: Element;
  options: A11yScanOptions;
} | null {
  if (!editor || !editor.editable) return null;
  const editable = editor.editable();
  if (!editable) return null;
  const editableEl: Element = editable.$;
  const frame = resolveCkeIframe(editor);
  if (frame?.contentDocument) {
    return {
      element: frame.contentDocument.documentElement,
      options: { frame }
    };
  }
  return { element: editableEl, options: {} };
}

async function scanEditor(editorName: string): Promise<void> {
  const cke = window.CKEDITOR;
  const editor = cke?.instances?.[editorName];
  if (!editor) {
    setState({
      isScanning: false,
      scanError: `CKEditor instance "${editorName}" not found`,
      lastTarget: null
    });
    return;
  }
  const target = resolveCkeTarget(editor);
  if (!target) {
    setState({
      isScanning: false,
      scanError: `CKEditor "${editorName}" has no editable content yet`,
      lastTarget: null
    });
    return;
  }
  lastEditorName = editorName;
  await runScan(target.element, target.options);
}

// Runs a scan and fires piA11yStateChange for badge updates, but does NOT
// touch lastTarget so the floating panel stays hidden if it wasn't already open.
async function runSilentScan(
  el: Element,
  options: A11yScanOptions = {}
): Promise<void> {
  const thisScan = ++scanSeq;
  setState({ isScanning: true }, /*skipRender=*/ true);
  try {
    const result = await runA11yScan(el, options);
    if (thisScan !== scanSeq) return;
    setState({ isScanning: false, scanResult: result }, /*skipRender=*/ true);
  } catch {
    if (thisScan !== scanSeq) return;
    setState({ isScanning: false }, /*skipRender=*/ true);
  }
}

async function scanEditorBadgeOnly(editorName: string): Promise<void> {
  const cke = window.CKEDITOR;
  const editor = cke?.instances?.[editorName];
  if (!editor) return;
  const target = resolveCkeTarget(editor);
  if (!target) return;

  // Capture whether the panel is currently open for this editor BEFORE we
  // update lastEditorName, so we use the previous value for the comparison.
  const panelOpenForThisEditor =
    state.lastTarget !== null && lastEditorName === editorName;
  lastEditorName = editorName;

  if (panelOpenForThisEditor) {
    // Panel is visible for this editor — do a full scan to keep it in sync.
    await runScan(target.element, target.options);
  } else {
    // Panel is hidden — scan silently so the badge updates without opening
    // the panel.
    await runSilentScan(target.element, target.options);
  }
}

// -----------------------------------------------------------------------------
// Button mount helper for legacy hosts.
// -----------------------------------------------------------------------------

const buttonRoots = new WeakMap<HTMLElement, Root>();

function mountButton(
  host: HTMLElement,
  opts: {
    onClick: () => void;
    getState: () => { busy: boolean; active: boolean };
  }
): () => void {
  let root = buttonRoots.get(host);
  if (!root) {
    root = createRoot(host);
    buttonRoots.set(host, root);
  }
  const render = () => {
    const { busy, active } = opts.getState();
    root!.render(
      <A11yCheckButton busy={busy} active={active} onClick={opts.onClick} />
    );
  };
  render();
  const handler = () => render();
  window.addEventListener("piA11yStateChange", handler);
  return () => {
    window.removeEventListener("piA11yStateChange", handler);
    root!.unmount();
    buttonRoots.delete(host);
  };
}

// -----------------------------------------------------------------------------
// Install on window.
// -----------------------------------------------------------------------------

const api: PiA11yApi = {
  scanEditor,
  scanEditorBadgeOnly,
  scanElement: runScan,
  scanHtmlFragment,
  sanitizeScanHtml,
  closePanel,
  mountButton,
  getLastResult() {
    return {
      scanResult: state.scanResult,
      isScanning: state.isScanning,
      lastEditorName
    };
  },
  get version() {
    return stateVersion;
  }
};

// Merge into any existing window.piA11y (preserves legacy `_test` helpers
// installed by /Scripts/CkEditor4/plugins/piaccessibility/plugin.js for the
// EmailCampaignUI on-save panel until that surface is migrated).
const existing = window.piA11y as
  | (Partial<PiA11yApi> & Record<string, unknown>)
  | undefined;
window.piA11y = Object.assign({}, existing ?? {}, api) as PiA11yApi;
